import { useEffect, useState } from "react";
import { Plus, Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";

const inp = "h-9 rounded-lg border border-slate-800 bg-slate-900/70 px-2 text-sm text-slate-100 outline-none focus:border-amber-500/70";

function PresetRow({ p, i, onList }) {
  const [f, setF] = useState({ name: p.name, multiplier: String(p.multiplier) });
  const call = async (fn, msg) => {
    try { onList((await fn()).data); toast.success(msg); } catch (e) { toast.error(errorMessage(e)); }
  };
  const dirty = f.name !== p.name || Number(f.multiplier) !== p.multiplier;
  return (
    <tr data-testid={`markup-preset-row-${i}`} className="border-t border-slate-800/70">
      <td className="py-2 pr-2"><input data-testid={`markup-preset-name-${i}`} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${inp} w-48`} /></td>
      <td className="pr-2"><input data-testid={`markup-preset-multiplier-${i}`} inputMode="decimal" value={f.multiplier} onChange={(e) => setF({ ...f, multiplier: e.target.value })} className={`${inp} w-24 text-right font-mono-num`} /></td>
      <td className="whitespace-nowrap text-right">
        {dirty && <button data-testid={`markup-preset-save-${i}`} onClick={() => call(() => api.put(`/settings/markup-presets/${p.id}`, { name: f.name, multiplier: Number(f.multiplier) }), "Preset saved. Existing Drafts change on Reprice.")}
          className="mr-2 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-slate-950 hover:bg-amber-400">Save</button>}
        {p.is_default ? (
          <span data-testid={`markup-preset-default-${i}`} className="mr-2 inline-flex items-center gap-1 text-xs font-semibold text-amber-300"><Star className="h-3.5 w-3.5" /> Default</span>
        ) : (
          <>
            <button data-testid={`markup-preset-make-default-${i}`} onClick={() => call(() => api.put(`/settings/markup-presets/${p.id}/default`), `${p.name} is now the default`)}
              className="mr-2 text-xs text-slate-400 hover:text-amber-200">Make default</button>
            <button aria-label="Delete" data-testid={`markup-preset-delete-${i}`} onClick={() => call(() => api.delete(`/settings/markup-presets/${p.id}`), `Deleted ${p.name}`)}
              className="p-1 text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </>
        )}
      </td>
    </tr>
  );
}

export function MarkupPresetsSection() {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", multiplier: "" });
  useEffect(() => { api.get("/markup-presets").then((r) => setList(r.data)).catch(() => {}); }, []);
  const add = async () => {
    if (!f.name.trim() || !(Number(f.multiplier) > 0)) return toast.error("Enter a name and a multiplier above 0");
    try {
      setList((await api.post("/settings/markup-presets", { name: f.name, multiplier: Number(f.multiplier) })).data);
      setF({ name: "", multiplier: "" });
      toast.success("Preset added");
    } catch (e) { toast.error(errorMessage(e)); }
  };
  return (
    <section data-testid="markup-presets-section" className="rounded-2xl border border-slate-800 p-5">
      <h2 className="text-base font-bold text-slate-100 md:text-lg">Markup presets</h2>
      <p className="mt-1 text-sm text-slate-400">Each customer gets a preset; new estimates use its multiplier (suggested = cost × multiplier). Editing a preset changes new Drafts only. Existing Drafts change on Reprice, and sent or approved estimates never change.</p>
      <table className="mt-4 w-auto text-sm"><tbody>{list.map((p, i) => <PresetRow key={`${p.id}:${p.name}:${p.multiplier}:${p.is_default}`} p={p} i={i} onList={setList} />)}</tbody></table>
      <div className="mt-3 flex flex-wrap gap-2">
        <input data-testid="markup-preset-new-name" placeholder="e.g. Contractor" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={`${inp} w-48`} />
        <input data-testid="markup-preset-new-multiplier" placeholder="× 1.2" inputMode="decimal" value={f.multiplier} onChange={(e) => setF({ ...f, multiplier: e.target.value })} className={`${inp} w-24 text-right font-mono-num`} />
        <button data-testid="markup-preset-add" onClick={add} className="inline-flex h-9 items-center gap-1.5 rounded-full border border-amber-500/40 px-4 text-sm text-amber-300 hover:bg-amber-500/10"><Plus className="h-4 w-4" /> Add preset</button>
      </div>
    </section>
  );
}
